/**
 * The admin panel shell (§9), gated on `user.isAdmin` from `GET /me`.
 *
 * The gate here is a *courtesy*, not a control. Every `/admin/*` route checks
 * `requireAdmin` server-side and answers **404** — not 403 — to anyone else, so
 * an admin route does not advertise its own existence (routes/admin.ts). Editing
 * this component's condition in devtools reveals nothing but empty panels and
 * failed requests.
 *
 * Note also where `isAdmin` comes from: the API reports it from a *live* check
 * that unions the `users.is_admin` flag with the `ADMIN_EMAILS` allowlist
 * matched against the verified token claim (auth/context.ts). So a bootstrap
 * admin sees this nav without their database row ever being touched — which is
 * the whole reason the allowlist exists.
 */

import { useState } from 'react';
import { BarChart3, ShieldCheck, Users } from 'lucide-react';

import { useMe } from '../api/hooks';
import { useAuth } from '../auth/AuthProvider';
import { Empty, ErrorState, Loading } from '../components/states';
import { Eyebrow } from '../components/primitives';
import { AdminCosts } from './Costs';
import { AdminStats } from './Stats';
import { AdminUsers } from './Users';

type Tab = 'stats' | 'users';

/** The windows the API accepts (`statsQuery`/`costsQuery`: 7–365). */
const WINDOWS = [7, 30, 90] as const;

export function AdminPanel() {
  const { isSignedIn } = useAuth();
  const me = useMe(isSignedIn);
  const [tab, setTab] = useState<Tab>('stats');
  const [days, setDays] = useState<number>(90);

  if (me.isPending) return <Loading label="Checking access…" />;
  if (me.isError) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;

  if (!me.data?.user.isAdmin) {
    return (
      <div className="screen">
        <Empty
          icon={<ShieldCheck size={20} />}
          title="Admin only"
          hint="Your account does not have access to the admin panel."
        />
      </div>
    );
  }

  return (
    <div className="screen">
      <Eyebrow>Internal</Eyebrow>
      <h1 className="h1">Admin</h1>
      <p className="lede">
        User management, platform stats and AWS spend. Signed in as <b className="c-ink">{me.data.user.email}</b>.
      </p>

      <div className="admin-nav">
        <button className={'tlf-btn' + (tab === 'stats' ? ' tlf-on' : '')} onClick={() => setTab('stats')}>
          <BarChart3 size={13} /> Stats &amp; costs
        </button>
        <button className={'tlf-btn' + (tab === 'users' ? ' tlf-on' : '')} onClick={() => setTab('users')}>
          <Users size={13} /> Users
        </button>

        {tab === 'stats' && (
          <div className="admin-window">
            {WINDOWS.map((w) => (
              <button key={w} className={'tlf-btn' + (days === w ? ' tlf-on' : '')} onClick={() => setDays(w)}>
                {w}d
              </button>
            ))}
          </div>
        )}
      </div>

      {tab === 'users' ? (
        <AdminUsers />
      ) : (
        <>
          <AdminStats days={days} />
          {/**
           * Rendered last and independently: it fetches its own data, and when
           * costs are unavailable it renders nothing at all. Stats never wait
           * on it and never fail with it — same "lazy, tolerant" placement
           * DragDuro gives its cost card.
           */}
          <AdminCosts days={days} />
        </>
      )}
    </div>
  );
}

